// Iter 91j — Per-posting rejection email editor.
//
// Recruiters can tweak the copy that goes out when a candidate is moved
// to "Rejected". Empty saves clear the per-posting override, falling
// back to the global default (Settings → Platform → Job Board sandbox).
//
// Backend contract:
//   GET  /jobs/{posting_id}/rejection-template  → { template, using_override, global_default, sandbox, tokens }
//   PUT  /jobs/{posting_id}/rejection-template  → { template } (empty string clears)
//   POST /jobs/{posting_id}/rejection-template/preview → { subject, body, html, sandbox }

import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, RotateCcw, Info, MailCheck, ShieldAlert } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

// Simple client-side token render — mirrors the backend `_render_template`
// so the live preview matches the audit row/email that will actually
// be sent. Only used when we can't (yet) hit the backend preview.
function renderLocal(tpl, ctx) {
  let out = tpl || "";
  Object.entries(ctx || {}).forEach(([k, v]) => {
    out = out.replace(new RegExp(`{{\\s*${k}\\s*}}`, "g"), String(v || ""));
  });
  return out;
}

const SAMPLE_CTX = {
  name:       "Alex Candidate",
  email:      "candidate@example.com",
  title:      "the role",
  department: "your department",
};

export function RejectionTemplateDialog({ open, onOpenChange, posting, onSaved }) {
  const [loading, setLoading]   = useState(false);
  const [saving,  setSaving]    = useState(false);
  const [meta,    setMeta]      = useState(null);   // { global_default, sandbox, tokens, using_override }
  const [draft,   setDraft]     = useState("");

  useEffect(() => {
    if (!open || !posting) return;
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const { data } = await api.get(`/jobs/${posting.posting_id}/rejection-template`);
        if (!alive) return;
        setMeta(data);
        // If there's no override yet, seed the editor with the global
        // default so recruiters can tweak from a known good starting point
        // (Save-with-no-change won't override — we detect that below).
        setDraft(data.template || data.global_default || "");
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load template");
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [open, posting]);

  // Preview context uses the posting's real title/department so the
  // recruiter sees exactly what the candidate will get.
  const previewCtx = useMemo(() => ({
    ...SAMPLE_CTX,
    title:      posting?.title || SAMPLE_CTX.title,
    department: posting?.department_name || SAMPLE_CTX.department,
  }), [posting]);

  const previewBody = useMemo(
    () => renderLocal(draft || (meta?.global_default || ""), previewCtx),
    [draft, meta, previewCtx],
  );

  const dirty = meta ? draft !== (meta.template || meta.global_default) : false;
  const wouldClear = meta && meta.using_override
    && draft.trim() === (meta.global_default || "").trim();

  const save = async () => {
    if (!posting) return;
    try {
      setSaving(true);
      // Save exactly what the recruiter typed. Server treats empty
      // string as "clear override, fall back to global default".
      const payload = { template: (draft || "").trim() };
      const { data } = await api.put(
        `/jobs/${posting.posting_id}/rejection-template`, payload);
      setMeta(data);
      setDraft(data.template || data.global_default || "");
      toast.success(data.using_override
        ? "Rejection copy saved for this posting"
        : "Reverted to the global default");
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const resetToDefault = () => {
    if (!meta) return;
    setDraft(meta.global_default || "");
  };

  const clearOverride = async () => {
    if (!posting) return;
    if (!window.confirm(
      "Clear this posting's rejection copy override? Candidates will get the global default going forward."
    )) return;
    try {
      setSaving(true);
      const { data } = await api.put(
        `/jobs/${posting.posting_id}/rejection-template`, { template: "" });
      setMeta(data);
      setDraft(data.global_default || "");
      toast.success("Cleared — using global default");
      onSaved?.(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Clear failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto"
                     data-testid="rejection-template-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MailCheck className="h-4 w-4 text-primary" />
            Rejection email — {posting?.title}
          </DialogTitle>
          <DialogDescription>
            Sent automatically when a candidate on this posting is moved to
            &ldquo;Rejected&rdquo;. Empty override falls back to the global default.
          </DialogDescription>
        </DialogHeader>

        {loading || !meta ? (
          <div className="py-10 flex justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="space-y-3">
            {/* Sandbox / status banner */}
            <div className={`rounded-md border p-2.5 text-xs flex items-start gap-2 ${
              meta.sandbox
                ? "border-amber-200 bg-amber-50 text-amber-800"
                : "border-emerald-200 bg-emerald-50 text-emerald-800"}`}
                 data-testid="rejection-template-sandbox-banner">
              {meta.sandbox
                ? <ShieldAlert className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                : <MailCheck className="h-3.5 w-3.5 mt-0.5 shrink-0" />}
              <div>
                {meta.sandbox
                  ? "Job Board is in sandbox — this copy is audit-logged only. Flip sandbox off in Settings → Platform → Job Board to send real emails."
                  : "Job Board is LIVE — this copy will send to real candidates when you flip them to Rejected."}
              </div>
            </div>

            {/* Token hint */}
            <div className="rounded-md border border-indigo-100 bg-indigo-50/60 p-2.5 text-xs text-indigo-900 flex items-start gap-2">
              <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <div>
                Tokens auto-swap on send:&nbsp;
                {(meta.tokens || []).map((t) => (
                  <code key={t}
                        className="mx-0.5 rounded bg-white px-1 py-0.5 font-mono text-[10px] text-indigo-800 border border-indigo-100">
                    {`{{${t}}}`}
                  </code>
                ))}
              </div>
            </div>

            {/* Editor */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>Copy</Label>
                {meta.using_override
                  ? <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-bold uppercase text-indigo-700"
                          data-testid="rejection-template-override-badge">
                      Custom for this posting
                    </span>
                  : <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-600"
                          data-testid="rejection-template-default-badge">
                      Using global default
                    </span>}
              </div>
              <Textarea rows={11}
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        placeholder={meta.global_default}
                        data-testid="rejection-template-textarea"
                        className="font-mono text-xs" />
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" size="sm" onClick={resetToDefault}
                        disabled={saving}
                        data-testid="rejection-template-reset-btn"
                        className="gap-1 text-[11px]">
                  <RotateCcw className="h-3 w-3" /> Reset to global default
                </Button>
                {meta.using_override && (
                  <Button variant="outline" size="sm" onClick={clearOverride}
                          disabled={saving}
                          data-testid="rejection-template-clear-btn"
                          className="gap-1 text-[11px] text-rose-600 border-rose-200 hover:bg-rose-50">
                    Clear override
                  </Button>
                )}
                {wouldClear && (
                  <span className="text-[10px] text-slate-500"
                        data-testid="rejection-template-clear-hint">
                    Saving this will revert to the global default.
                  </span>
                )}
              </div>
            </div>

            {/* Live preview */}
            <div className="space-y-1.5">
              <Label>Preview</Label>
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-800 whitespace-pre-wrap"
                   data-testid="rejection-template-preview">
                <div className="mb-1 text-[10px] font-bold uppercase text-slate-500">
                  Subject: Your application for {previewCtx.title}
                </div>
                {previewBody || (
                  <span className="italic text-slate-400">
                    Preview will show once you start typing (or reset to default).
                  </span>
                )}
              </div>
              <div className="text-[10px] text-slate-400">
                Preview uses sample candidate <b>{previewCtx.name}</b>. The real send
                pulls the actual candidate name/email off the resume record.
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving || loading || !dirty}
                  data-testid="rejection-template-save-btn" className="gap-1.5">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
